import React, { useState } from 'react';
import { FaPhone, FaMapMarkerAlt, FaClock, FaComments, FaCheck, FaRedo, FaBolt } from 'react-icons/fa';
import { formatDistanceToNow, format } from 'date-fns';
import { anonymizeConversation } from '../../utils/demoMode';
import './ConversationCard.scss';

// Helper function to lighten a hex color
const lightenColor = (color, percent = 85) => {
    const num = parseInt(color.replace('#', ''), 16);
    const r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
    const g = ((num >> 8) & 0x00FF) + Math.round((255 - ((num >> 8) & 0x00FF)) * (percent / 100));
    const b = (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * (percent / 100));
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

const ConversationCard = ({
    conversation,
    onDragStart,
    onDragEnd,
    onQuickAction,
    onCreateReminderFlow,
    onViewConversation,
    isDragging,
    isCreatingReminderFlow,
    technician,
    canEdit
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [flowFeedback, setFlowFeedback] = useState(null);

    // Anonymize conversation data for demo mode
    const displayConversation = anonymizeConversation(conversation);

    const formatPhone = (phone) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
            return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        }
        return phone;
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: '#6c757d',
            confirmed: '#28a745',
            needs_reschedule: '#ffc107',
            closed: '#17a2b8'
        };
        return colors[status] || '#6c757d';
    };

    const getRelativeTime = (timestamp) => {
        if (!timestamp) return '';
        return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    };

    const getAppointmentDate = (workDate, timeWindow) => {
        if (!workDate) return timeWindow || '';

        let date = null;
        if (typeof workDate === 'string') {
            const dateMatch = workDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (dateMatch) {
                const [, year, month, day] = dateMatch;
                // Parse the calendar date locally so date-only values do not shift by timezone.
                date = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
            }
        }

        if (!date) {
            date = new Date(workDate);
        }

        if (Number.isNaN(date.getTime())) {
            return timeWindow || '';
        }

        return `${format(date, 'MMM d, yyyy')} - ${timeWindow || 'TBD'}`;
    };

    const handleCardClick = (e) => {
        e.preventDefault();
        if (onViewConversation) {
            onViewConversation(conversation);
        } else {
            setShowDetails(!showDetails);
        }
    };

    const handleQuickActionClick = (e, action) => {
        e.stopPropagation();
        onQuickAction(conversation.id, action);
    };

    const handleCreateReminderFlowClick = async (e) => {
        e.stopPropagation();

        if (!conversation.appointment || !onCreateReminderFlow || isCreatingReminderFlow) {
            return;
        }

        try {
            setFlowFeedback(null);
            const response = await onCreateReminderFlow(conversation.id);
            setFlowFeedback({
                type: response?.success ? 'success' : 'error',
                message: response?.message || (response?.success ? 'Reminder flow created.' : 'Failed to create reminder flow.')
            });
        } catch (error) {
            setFlowFeedback({
                type: 'error',
                message: error.message || 'Failed to create reminder flow.'
            });
        }
    };

    return (
        <div
            className={`conversation-card ${isDragging ? 'conversation-card--dragging' : ''}`}
            draggable={canEdit}
            onDragStart={(e) => canEdit && onDragStart(e, conversation)}
            onDragEnd={onDragEnd}
            onClick={handleCardClick}
        >
            <div className="conversation-card__header">
                <div className="customer-info">
                    <h4 className="customer-name">{conversation.contact.name}</h4>
                    <div className="customer-details">
                        {conversation.contact.phone && (
                            <span className="phone">
                                <FaPhone className="icon" />
                                {formatPhone(conversation.contact.phone)}
                            </span>
                        )}
                        <span className="location">
                            <FaMapMarkerAlt className="icon" />
                            {displayConversation.contact.location.address1}
                            {displayConversation.contact.location.address2 && `, ${displayConversation.contact.location.address2}`}
                            {', '}
                            {displayConversation.contact.location.city}, {displayConversation.contact.location.state}
                        </span>
                    </div>
                </div>

                <div className="conversation-status">
                    {conversation.appointment && onCreateReminderFlow && (
                        <button
                            type="button"
                            className="flow-trigger-btn"
                            onClick={handleCreateReminderFlowClick}
                            disabled={isCreatingReminderFlow}
                            title="Create reminder flow for this appointment"
                        >
                            <FaBolt />
                        </button>
                    )}
                    <div
                        className="status-indicator"
                        style={{ backgroundColor: getStatusColor(displayConversation.status) }}
                    ></div>
                    {displayConversation.unreadCount > 0 && (
                        <span className="unread-badge">{displayConversation.unreadCount}</span>
                    )}
                </div>
            </div>

            {displayConversation.appointment && (
                <div className="conversation-card__appointment">
                    <div className="appointment-info">
                        <div className="service">
                            <strong>
                                {displayConversation.appointment.serviceDescription ||
                                 displayConversation.appointment.metadata?.subscription?.service_type ||
                                 displayConversation.appointment.metadata?.Description ||
                                 `Service ${displayConversation.appointment.serviceCode || ''}`}
                            </strong>
                        </div>
                        <div className="appointment-date">
                            <FaClock className="icon" />
                            {getAppointmentDate(
                                displayConversation.appointment.workDate,
                                displayConversation.appointment.timeWindow
                            )}
                        </div>
                        {displayConversation.technician && (
                            <div className="technician-info">
                                <strong>Technician:</strong> {displayConversation.technician.name}
                            </div>
                        )}
                        {displayConversation.appointment.metadata?.ServiceInstructions && (
                            <div className="service-instructions">
                                <strong>Service Instructions:</strong>
                                <p>{displayConversation.appointment.metadata.ServiceInstructions}</p>
                            </div>
                        )}
                        {displayConversation.appointment.metadata?.LocationInstructions && (
                            <div className="location-instructions">
                                <strong>Location Instructions:</strong>
                                <p>{displayConversation.appointment.metadata.LocationInstructions}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {flowFeedback && (
                <div className={`conversation-card__flow-feedback conversation-card__flow-feedback--${flowFeedback.type}`}>
                    {flowFeedback.message}
                </div>
            )}

            {displayConversation.lastMessage && (
                <div className="conversation-card__message">
                    <div className="message-content">
                        <FaComments className="icon" />
                        <span className="message-text">
                            {displayConversation.lastMessage.body.length > 80
                                ? `${displayConversation.lastMessage.body.substring(0, 80)}...`
                                : displayConversation.lastMessage.body
                            }
                        </span>
                    </div>
                    <div className="message-time">
                        {getRelativeTime(displayConversation.lastMessage.timestamp)}
                    </div>
                </div>
            )}

            {canEdit && (
                <div className="conversation-card__actions">
                    {conversation.status === 'pending' && (
                        <>
                            <button
                                className="action-btn action-btn--confirm"
                                onClick={(e) => handleQuickActionClick(e, 'confirm')}
                                title="Mark as Confirmed"
                                style={{
                                    backgroundColor: lightenColor('#00a9eb', 85),
                                    color: '#00a9eb'
                                }}
                            >
                                <FaCheck />
                            </button>
                            <button
                                className="action-btn action-btn--reschedule"
                                onClick={(e) => handleQuickActionClick(e, 'reschedule')}
                                title="Needs Reschedule"
                                style={{
                                    backgroundColor: lightenColor('#00ebcb', 85),
                                    color: '#00ebcb'
                                }}
                            >
                                <FaRedo />
                            </button>
                        </>
                    )}
                    {conversation.status === 'needs_reschedule' && (
                        <button
                            className="action-btn action-btn--confirm"
                            onClick={(e) => handleQuickActionClick(e, 'confirm')}
                            title="Mark as Confirmed"
                            style={{
                                backgroundColor: lightenColor('#00a9eb', 85),
                                color: '#00a9eb'
                            }}
                        >
                            <FaCheck />
                        </button>
                    )}
                </div>
            )}

            {showDetails && (
                <div className="conversation-card__details">
                    <div className="detail-section">
                        <h5>Full Address</h5>
                        <p>
                            {displayConversation.contact.location.address1}
                            <br />
                            {displayConversation.contact.location.city}, {displayConversation.contact.location.state}
                        </p>
                    </div>

                    {displayConversation.appointment.instructions && (
                        <div className="detail-section">
                            <h5>Special Instructions</h5>
                            <p>{displayConversation.appointment.instructions}</p>
                        </div>
                    )}

                    <div className="detail-section">
                        <h5>Conversation History</h5>
                        <div className="message-thread">
                            <div className="message-item">
                                <span className="message-direction">
                                    {displayConversation.lastMessage.direction === 'outbound' ? 'Sent' : 'Received'}
                                </span>
                                <span className="message-text">{displayConversation.lastMessage.body}</span>
                                <span className="message-timestamp">
                                    {format(new Date(displayConversation.lastMessage.timestamp), 'MMM d, h:mm a')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConversationCard;
